import { useEffect, useLayoutEffect, useState } from "react";

export type Theme = "light" | "dark";
export type ThemePreference = Theme | "system";
export const THEME_PREFERENCE_KEY = "omer-theme-preference";
const systemThemeQuery = "(prefers-color-scheme: dark)";

export function readThemePreference(storage?: Pick<Storage, "getItem">): ThemePreference {
  try {
    const stored = (storage ?? window.localStorage).getItem(THEME_PREFERENCE_KEY);
    return stored === "light" || stored === "dark" ? stored : "system";
  } catch {
    return "system";
  }
}

export function resolveTheme(preference: ThemePreference, prefersDark: boolean): Theme {
  return preference === "system" ? prefersDark ? "dark" : "light" : preference;
}

export function initialTheme(): Theme {
  return resolveTheme(readThemePreference(), window.matchMedia(systemThemeQuery).matches);
}

export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(readThemePreference);
  const [prefersDark, setPrefersDark] = useState(() => window.matchMedia(systemThemeQuery).matches);
  const theme = resolveTheme(preference, prefersDark);

  useEffect(() => {
    const query = window.matchMedia(systemThemeQuery);
    const followSystem = () => setPrefersDark(query.matches);
    const followStorage = (event: StorageEvent) => {
      if (event.key === THEME_PREFERENCE_KEY || event.key === null) setPreference(readThemePreference());
    };
    followSystem();
    query.addEventListener("change", followSystem);
    window.addEventListener("storage", followStorage);
    return () => {
      query.removeEventListener("change", followSystem);
      window.removeEventListener("storage", followStorage);
    };
  }, []);

  useLayoutEffect(() => { document.documentElement.dataset.theme = theme; }, [theme]);

  function selectTheme(nextTheme: Theme) {
    setPreference(nextTheme);
    try { window.localStorage.setItem(THEME_PREFERENCE_KEY, nextTheme); } catch { /* The choice still applies for this visit. */ }
  }

  return [theme, selectTheme] as const;
}
